import { IHero } from "@src/components/Hero";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import { getCookie } from "cookies-next";
import { useTransform, useViewportScroll, motion } from "framer-motion";
import Image from "next/image";
import React from "react";
import image from "../../../../public/images/t10/hero.jpg";
import Icon from "@src/components/Icon";

const Hero: React.FC<IHero> = ({
  eventId,
  heroImageData,
  slugData,
  loading,
  eventUuid,
  albumDetailsData,
}) => {
  const { scrollY } = useViewportScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 60]);
  const currentAccessToken = getCookie("tsl") as string;
  const [show, setShow] = React.useState<boolean>(false);

  const mediaData = albumDetailsData?.getMedia || [];

  React.useEffect(() => {
    setShow(true);
  }, []);

  return (
    <div className="h-[100vh] relative overflow-hidden">
      <motion.div
        initial={{ scale: 1 }}
        animate={{
          // scale: 1.1,
          transition: {
            delay: 0.3,
            duration: 0.8,
            ease: [0.79, 0.14, 0.15, 0.86],
          },
        }}
        className="min-h-screen flex flex-col sm:flex-row justify-start items-center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{
            opacity: 1,
            transition: {
              delayChildren: 1,
              staggerChildren: 1.5,
            },
          }}
          viewport={{ once: true }}
          className="flex flex-col h-[30vh] sm:h-screen w-full sm:w-[35%] sm:pl-4 order-2 sm:order-1"
        >
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: [0.79, 0.14, 0.15, 0.86],
              },
            }}
            className="hidden sm:flex absolute top-0 left-0 p-2 sm:p-4"
          >
            <div className="w-full md:px-6 flex justify-center items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: {
                    delay: 0.2,
                    duration: 0.6,
                  },
                }}
                className="text-black uppercase font-inter tracking-[0.2rem] my-2 text-base md:text-lg"
              >
                {loading
                  ? "..."
                  : !!slugData?.eventBySlug && !!slugData?.eventBySlug?.hash_tag
                  ? slugData?.eventBySlug?.hash_tag
                  : ""}
              </motion.div>
            </div>
          </motion.header>

          <div className="flex flex-col justify-end h-2/3 p-4 sm:p-8 gap-2">
            <h1 className="font-brooklyn text-3xl sm:text-6xl text-black tracking-wider break-words">
              {slugData?.eventBySlug?.name}
            </h1>

            <motion.h5
              initial={{ opacity: 0, y: 30 }}
              whileInView={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 1,
                  ease: [0.79, 0.14, 0.15, 0.86],
                },
              }}
              className="text-base sm:text-lg text-[#4C4B4B] font-brooklyn break-words"
            >
              {slugData?.eventBySlug?.welcome_msg}
            </motion.h5>

            <motion.h5
              initial={{ opacity: 0, y: 30 }}
              whileInView={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 1,
                  ease: [0.79, 0.14, 0.15, 0.86],
                },
              }}
              className="sm:hidden text-base uppercase font-black tracking-widest sm:text-lg text-black font-brooklyn break-words"
            >
              {loading
                ? "..."
                : !!slugData?.eventBySlug && !!slugData?.eventBySlug?.hash_tag
                ? slugData?.eventBySlug?.hash_tag
                : ""}
            </motion.h5>
          </div>

          <div className="h-1/2 sm:flex"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{
            opacity: 1,
            transition: {
              delayChildren: 1,
              staggerChildren: 1.5,
            },
          }}
          viewport={{ once: true }}
          className="flex flex-col h-[70vh] sm:h-[100vh] order-1 sm:order-2 gap-4 items-center w-full sm:w-[65%] relative overflow-hidden"
        >
          {!!mediaData && mediaData.length > 0 ? (
            <div className="relative w-full h-full overflow-hidden flex justify-center sm:justify-between gap-2 sm:gap-6">
              {/* First Column - Top to Bottom */}
              <div
                className="flex flex-col gap-4 animate-vertical-scroll-down"
                style={{
                  height: `${
                    mediaData.length * (192 + 8) + (mediaData.length - 1) * 8
                  }px`, // Mobile: 14rem (192px) + 8px gap, Desktop: 28rem (384px) + 16px gap
                }}
              >
                {/* First set of images */}
                {mediaData.map((image, index) => {
                  if (image && image.meta?.s3_url) {
                    return (
                      <div
                        key={`first-col1-${index}`}
                        className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                        style={{
                          boxShadow:
                            "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                        }}
                      >
                        <div className="relative w-full h-full rounded-sm overflow-hidden">
                          <Image
                            placeholder="blur"
                            blurDataURL={`data:image/svg+xml;base64,${toBase64(
                              shimmer(700, 475)
                            )}`}
                            src={image.meta.s3_url}
                            layout="fill"
                            alt="gallery"
                            objectFit="cover"
                            className="object-center"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
                {/* Duplicate set for seamless loop */}
                {mediaData.map((image, index) => {
                  if (image && image.meta?.s3_url) {
                    return (
                      <div
                        key={`second-col1-${index}`}
                        className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                        style={{
                          boxShadow:
                            "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                        }}
                      >
                        <div className="relative w-full h-full rounded-sm overflow-hidden">
                          <Image
                            placeholder="blur"
                            blurDataURL={`data:image/svg+xml;base64,${toBase64(
                              shimmer(700, 475)
                            )}`}
                            src={image.meta.s3_url}
                            layout="fill"
                            alt="gallery"
                            objectFit="cover"
                            className="object-center"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Second Column - Bottom to Top */}
              <div
                className="flex flex-col gap-4 animate-vertical-scroll-up"
                style={{
                  height: `${
                    mediaData.length * (192 + 8) + (mediaData.length - 1) * 8
                  }px`,
                }}
              >
                {/* First set of images */}
                {mediaData.map((image, index) => {
                  if (image && image.meta?.s3_url) {
                    return (
                      <div
                        key={`first-col2-${index}`}
                        className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                        style={{
                          boxShadow:
                            "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                        }}
                      >
                        <div className="relative w-full h-full rounded-sm overflow-hidden">
                          <Image
                            placeholder="blur"
                            blurDataURL={`data:image/svg+xml;base64,${toBase64(
                              shimmer(700, 475)
                            )}`}
                            src={image.meta.s3_url}
                            layout="fill"
                            alt="gallery"
                            objectFit="cover"
                            className="object-center"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
                {/* Duplicate set for seamless loop */}
                {mediaData.map((image, index) => {
                  if (image && image.meta?.s3_url) {
                    return (
                      <div
                        key={`second-col2-${index}`}
                        className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                        style={{
                          boxShadow:
                            "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                        }}
                      >
                        <div className="relative w-full h-full rounded-sm overflow-hidden">
                          <Image
                            placeholder="blur"
                            blurDataURL={`data:image/svg+xml;base64,${toBase64(
                              shimmer(700, 475)
                            )}`}
                            src={image.meta.s3_url}
                            layout="fill"
                            alt="gallery"
                            objectFit="cover"
                            className="object-center"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Third Column - Top to Bottom (Hidden on mobile) */}
              <div
                className="flex flex-col gap-4 animate-vertical-scroll-down"
                style={{
                  height: `${
                    mediaData.length * 304 + (mediaData.length - 1) * 16
                  }px`,
                }}
              >
                {/* First set of images */}
                {mediaData.map((image, index) => {
                  if (image && image.meta?.s3_url) {
                    return (
                      <div
                        key={`first-col3-${index}`}
                        className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                        style={{
                          boxShadow:
                            "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                        }}
                      >
                        <div className="relative w-full h-full rounded-sm overflow-hidden">
                          <Image
                            placeholder="blur"
                            blurDataURL={`data:image/svg+xml;base64,${toBase64(
                              shimmer(700, 475)
                            )}`}
                            src={image.meta.s3_url}
                            layout="fill"
                            alt="gallery"
                            objectFit="cover"
                            className="object-center"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
                {/* Duplicate set for seamless loop */}
                {mediaData.map((image, index) => {
                  if (image && image.meta?.s3_url) {
                    return (
                      <div
                        key={`second-col3-${index}`}
                        className="w-[10rem] sm:w-[20rem] h-[14rem] sm:h-[28rem] bg-white p-3 pb-6 flex-shrink-0"
                        style={{
                          boxShadow:
                            "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                        }}
                      >
                        <div className="relative w-full h-full rounded-sm overflow-hidden">
                          <Image
                            placeholder="blur"
                            blurDataURL={`data:image/svg+xml;base64,${toBase64(
                              shimmer(700, 475)
                            )}`}
                            src={image.meta.s3_url}
                            layout="fill"
                            alt="gallery"
                            objectFit="cover"
                            className="object-center"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          ) : (
            <>
              <div
                className="w-[7rem] sm:w-[20rem] h-[8.5rem] sm:h-[28rem] bg-white p-1 pb-3 sm:p-3 sm:pb-6"
                style={{
                  boxShadow: "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                }}
              >
                <div className="bg-black w-full h-full"></div>
              </div>
              <div
                className="w-[7rem] sm:w-[20rem] h-[8.5rem] sm:h-[28rem] bg-white p-1 pb-3 sm:p-3 sm:pb-6"
                style={{
                  boxShadow: "0px 4.57px 4.57px 0px rgba(106, 105, 105, 0.25)",
                }}
              >
                <div className="bg-black w-full h-full"></div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>

      <style>{`
        @keyframes vertical-scroll-down {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-${
              mediaData.length * (window.innerWidth < 640 ? 200 : 304) +
              (mediaData.length - 1) * (window.innerWidth < 640 ? 8 : 16)
            }px);
          }
        }
        
        @keyframes vertical-scroll-up {
          0% {
            transform: translateY(-${
              mediaData.length * (window.innerWidth < 640 ? 200 : 304) +
              (mediaData.length - 1) * (window.innerWidth < 640 ? 8 : 16)
            }px);
          }
          100% {
            transform: translateY(0);
          }
        }
        
        .animate-vertical-scroll-down {
          animation: vertical-scroll-down ${
            mediaData.length * 6
          }s linear infinite;
        }
        
        .animate-vertical-scroll-up {
          animation: vertical-scroll-up ${
            mediaData.length * 6
          }s linear infinite;
        }
        
        .animate-vertical-scroll-down:hover,
        .animate-vertical-scroll-up:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default Hero;
